import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CreditCard, Heart, DollarSign, Mail, MapPin, AlertTriangle, ExternalLink, RefreshCw, Clock, CheckCircle, ArrowDown } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Donate = () => {
  const [balanceData, setBalanceData] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceError, setBalanceError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [upcomingCosts, setUpcomingCosts] = useState([]);
  const [costsLoading, setCostsLoading] = useState(true);
  const [selectedCost, setSelectedCost] = useState(null);
  const [donationAmount, setDonationAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [confirmedDonation, setConfirmedDonation] = useState(null);
  const [siteSettings, setSiteSettings] = useState({ show_bank_balance: true, show_upcoming_costs: true });

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      setSiteSettings(response.data);
    } catch (err) {
      console.error("Error fetching site settings");
    }
  };

  const fetchBalance = async (forceRefresh = false) => {
    try {
      if (forceRefresh) setRefreshing(true);
      else setBalanceLoading(true);
      
      const response = await axios.get(`${BACKEND_URL}/api/balance${forceRefresh ? '?force_refresh=true' : ''}`);
      setBalanceData(response.data);
      
      if (response.data.error && response.data.balance === null) {
        setBalanceError(response.data.error);
      } else {
        setBalanceError(null);
      }
    } catch (err) {
      setBalanceError("Unable to connect to server.");
    } finally {
      setBalanceLoading(false);
      setRefreshing(false);
    }
  };

  const fetchUpcomingCosts = async () => {
    try {
      setCostsLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/upcoming-costs`);
      setUpcomingCosts(response.data);
    } catch (err) {
      console.error("Error fetching upcoming costs:", err);
    } finally {
      setCostsLoading(false);
    }
  };

  useEffect(() => {
    fetchSiteSettings();
    fetchBalance();
    fetchUpcomingCosts();
  }, []);

  const formatBalance = (balance) => {
    if (balance === null || balance === undefined) return "$--";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(balance);
  };

  const formatDate = (isoString) => {
    if (!isoString) return "Never";
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isLowBalance = balanceData?.balance !== null && balanceData?.balance < 1000;

  const donationMethods = [
    {
      icon: CreditCard,
      title: "Credit/Debit Card",
      description: "Donate securely via Zeffy. 100% of your donation goes to the animals.",
      link: "https://www.zeffy.com/embed/donation-form/donate-to-make-a-difference-13204?modal=true",
      buttonText: "Donate with Card",
      featured: true,
    },
    {
      icon: Heart,
      title: "PayPal",
      description: "Use your PayPal account to make a secure donation.",
      link: "https://www.paypal.com/donate/?hosted_button_id=SPQN8WSG432Q4",
      buttonText: "Donate with PayPal",
    },
    {
      icon: DollarSign,
      title: "Venmo",
      description: "Quick and easy donations via Venmo.",
      link: "https://venmo.com/u/threeEyeBlindrescue",
      buttonText: "Donate with Venmo",
    },
  ];

  return (
    <div data-testid="donate-page">
      {/* Hero Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1666593198394-f78259ecf707?w=1920&h=800&fit=crop"
            alt="Volunteer with rescue dog"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/95 to-[#0A0A0A]/80" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Support Our Mission
            </span>
            <h1 className="font-['Bebas_Neue'] text-6xl lg:text-8xl text-white mb-6">
              Every Dollar
              <span className="text-[#FFD700] block">Saves Lives</span>
            </h1>
            <p className="text-xl text-[#A3A3A3] leading-relaxed">
              Your donation directly funds veterinary care, food, shelter, and rehabilitation for rescued animals. 100% of proceeds go to animal care.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Financial Transparency Section */}
      <section className="py-16 bg-[#0A0A0A] border-b border-[#4A5320]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Monthly Costs Info - Always shown */}
          {!siteSettings.show_bank_balance && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-tactical p-8 text-center max-w-xl mx-auto mb-8"
            >
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-[#4A5320] flex items-center justify-center">
                  <DollarSign className="w-8 h-8 text-[#FFD700]" />
                </div>
              </div>
              <h2 className="font-['Bebas_Neue'] text-2xl text-white mb-4">
                Support Our Mission
              </h2>
              <p className="text-[#A3A3A3] text-sm text-center">
                Our average monthly operating costs are <span className="text-[#FFD700] font-semibold">$17,000 per month</span> to cover basic needs, food, transport, training, routine medical care, and urgent medical care for our foster pets looking for their forever homes
              </p>
              <a
                href="https://customer-assets.emergentagent.com/job_eyeblind-v2/artifacts/q613lth5_Cash%20Flow%20%281%29.png"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 text-[#FFD700] text-xs font-mono uppercase tracking-widest hover:text-white transition-colors"
              >
                See Our Costs Breakdown
                <ExternalLink className="w-3 h-3" />
              </a>
            </motion.div>
          )}

          {(siteSettings.show_bank_balance || (siteSettings.show_upcoming_costs && upcomingCosts.length > 0)) && (
          <div className={`grid grid-cols-1 ${siteSettings.show_upcoming_costs && upcomingCosts.length > 0 && siteSettings.show_bank_balance ? 'lg:grid-cols-2' : ''} gap-8`}>
            {/* Bank Balance Card */}
            {siteSettings.show_bank_balance && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`card-tactical p-8 text-center ${!siteSettings.show_upcoming_costs || upcomingCosts.length === 0 ? 'max-w-xl mx-auto' : ''}`}
            >
              <div className="flex justify-center mb-4">
                <div className={`w-16 h-16 ${isLowBalance ? 'bg-red-900/50' : 'bg-[#4A5320]'} flex items-center justify-center`}>
                  <DollarSign className={`w-8 h-8 ${isLowBalance ? 'text-red-400' : 'text-[#FFD700]'}`} />
                </div>
              </div>

              <h2 className="font-['Bebas_Neue'] text-2xl text-white mb-1">
                Current Bank Account Balance
              </h2>
              <p className="text-[#A3A3A3] text-xs font-mono uppercase tracking-widest mb-6">
                Full Transparency • Updated Every 24-48 Hours
              </p>

              {balanceLoading ? (
                <div className="flex items-center justify-center gap-3 py-4">
                  <RefreshCw className="w-6 h-6 text-[#FFD700] animate-spin" />
                  <span className="text-[#A3A3A3] text-sm">Loading...</span>
                </div>
              ) : balanceError ? (
                <div className="py-4">
                  <p className="text-yellow-500 text-sm mb-2">Temporarily unavailable</p>
                  <button
                    onClick={() => fetchBalance(true)}
                    disabled={refreshing}
                    className="text-[#FFD700] font-mono text-xs uppercase tracking-widest hover:underline"
                  >
                    {refreshing ? 'Retrying...' : 'Try Again'}
                  </button>
                </div>
              ) : (
                <>
                  <div className={`font-['Bebas_Neue'] text-5xl lg:text-6xl mb-2 ${isLowBalance ? 'text-red-400' : 'text-[#FFD700]'}`}>
                    {formatBalance(balanceData?.balance)}
                  </div>

                  {isLowBalance && (
                    <div className="flex items-center justify-center gap-2 mb-4 p-3 bg-red-900/30 border border-red-700">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <span className="text-red-400 font-mono text-xs uppercase tracking-widest">
                        Funds Running Low
                      </span>
                    </div>
                  )}
                  
                  <p className="text-[#A3A3A3] text-xs mt-4 text-center">
                    Our average monthly operating costs are <span className="text-[#FFD700] font-semibold">$17,000 per month</span> to cover basic needs, food, transport, training, routine medical care, and urgent medical care for our foster pets looking for their forever homes
                  </p>
                  
                  <a
                    href="https://customer-assets.emergentagent.com/job_eyeblind-v2/artifacts/q613lth5_Cash%20Flow%20%281%29.png"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-4 text-[#FFD700] text-xs font-mono uppercase tracking-widest hover:text-white transition-colors"
                  >
                    See Our Costs Breakdown
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </>
              )}
            </motion.div>
            )}

            {/* Upcoming Costs Card - Only show if there are costs and setting is enabled */}
            {siteSettings.show_upcoming_costs && upcomingCosts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="card-tactical p-8"
            >
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-[#4A5320] flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-[#FFD700]" />
                </div>
              </div>

              <h2 className="font-['Bebas_Neue'] text-2xl text-white mb-1 text-center">
                Upcoming Costs
              </h2>
              
              <p className="text-[#A3A3A3] text-sm mb-6 text-center leading-relaxed">
                We have lots of regular operating costs, like food and routine medical care for our fosters. But here are a few of our urgent upcoming expenses we need extra help covering! Commit here to covering a cost and then donate at the links below
              </p>

              {costsLoading ? (
                <div className="flex items-center justify-center gap-3 py-4">
                  <RefreshCw className="w-6 h-6 text-[#FFD700] animate-spin" />
                  <span className="text-[#A3A3A3] text-sm">Loading...</span>
                </div>
              ) : upcomingCosts.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-[#A3A3A3] text-sm">No upcoming costs at this time.</p>
                  <p className="text-[#666] text-xs mt-2">Check back soon!</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {upcomingCosts.map((cost) => (
                    <div
                      key={cost.id}
                      className={`p-4 border ${cost.is_covered ? 'border-green-700 bg-green-900/20' : 'border-[#4A5320] bg-[#0A0A0A]'} transition-all`}
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <h4 className={`font-semibold ${cost.is_covered ? 'text-green-400 line-through' : 'text-white'}`}>
                            {cost.title}
                          </h4>
                          {cost.description && (
                            <p className={`text-xs mt-1 ${cost.is_covered ? 'text-green-600' : 'text-[#A3A3A3]'}`}>
                              {cost.description}
                            </p>
                          )}
                          {cost.due_date && (
                            <p className={`text-xs mt-1 ${cost.is_covered ? 'text-green-600' : 'text-[#666]'}`}>
                              Due: {cost.due_date}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className={`font-['Bebas_Neue'] text-2xl ${cost.is_covered ? 'text-green-400 line-through' : 'text-[#FFD700]'}`}>
                            ${cost.amount.toFixed(2)}
                          </span>
                          
                          {/* Show commitment progress */}
                          {!cost.is_covered && cost.committed_amount > 0 && (
                            <div className="mt-1">
                              <div className="text-xs text-green-400 font-mono">
                                ${cost.committed_amount.toFixed(2)} committed
                              </div>
                              <div className="text-xs text-[#666]">
                                {cost.commitment_count} {cost.commitment_count === 1 ? 'person' : 'people'}
                              </div>
                              {/* Progress bar */}
                              <div className="w-full h-1 bg-[#333] mt-1 overflow-hidden">
                                <div 
                                  className="h-full bg-green-500 transition-all duration-500"
                                  style={{ width: `${Math.min((cost.committed_amount / cost.amount) * 100, 100)}%` }}
                                />
                              </div>
                            </div>
                          )}
                          
                          {cost.is_covered ? (
                            <div className="flex items-center gap-1 mt-2 text-green-400">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-xs font-mono uppercase">Covered!</span>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                if (selectedCost === cost.id) {
                                  setSelectedCost(null);
                                  setDonationAmount("");
                                  setDonorName("");
                                  setConfirmedDonation(null);
                                } else {
                                  setSelectedCost(cost.id);
                                  setDonationAmount("");
                                  setDonorName("");
                                  setConfirmedDonation(null);
                                }
                              }}
                              className={`mt-2 text-xs font-mono uppercase tracking-widest border px-3 py-1 transition-all ${
                                selectedCost === cost.id 
                                  ? 'bg-[#FFD700] text-black border-[#FFD700]' 
                                  : 'text-[#FFD700] hover:text-white border-[#FFD700] hover:bg-[#FFD700] hover:text-black'
                              }`}
                            >
                              {selectedCost === cost.id ? 'Close' : 'Donate Towards This'}
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* Donation Amount Input */}
                      {selectedCost === cost.id && !cost.is_covered && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4 pt-4 border-t border-[#4A5320]"
                        >
                          {confirmedDonation === cost.id ? (
                            /* Confirmed - Show payment instructions */
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="bg-[#1A1C18] border border-[#FFD700] p-4"
                            >
                              <p className="text-[#FFD700] text-sm font-semibold mb-2 flex items-center gap-2">
                                <Heart className="w-4 h-4" />
                                Thank you{donorName ? `, ${donorName}` : ''}, for committing to donate ${parseFloat(donationAmount).toFixed(2)} towards {cost.title}!
                              </p>
                              <p className="text-[#A3A3A3] text-xs mb-3">
                                Please complete your donation using one of the payment methods below:
                              </p>
                              <div className="flex items-center gap-2 text-xs text-[#FFD700]">
                                <ArrowDown className="w-4 h-4 animate-bounce" />
                                <span className="font-mono uppercase tracking-widest">Credit/Debit, PayPal, Venmo, or Zelle</span>
                              </div>
                            </motion.div>
                          ) : (
                            /* Not confirmed - Show input and confirm button */
                            <>
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-[#A3A3A3] text-xs font-mono uppercase tracking-widest mb-2">
                                    Your Name (optional)
                                  </label>
                                  <input
                                    type="text"
                                    value={donorName}
                                    onChange={(e) => setDonorName(e.target.value)}
                                    placeholder="Enter your name"
                                    className="w-full px-4 py-2 bg-[#1A1C18] border border-[#4A5320] text-white text-sm focus:outline-none focus:border-[#FFD700]"
                                  />
                                  <p className="text-[#666] text-xs mt-1">
                                    Your name will not be displayed publicly
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-[#A3A3A3] text-xs font-mono uppercase tracking-widest mb-2">
                                    How much would you like to donate?
                                  </label>
                                  <div className="flex gap-2">
                                    <div className="relative flex-1">
                                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#666]" />
                                      <input
                                        type="number"
                                        min="1"
                                        step="0.01"
                                        value={donationAmount}
                                        onChange={(e) => setDonationAmount(e.target.value)}
                                        placeholder="Enter amount"
                                        className="w-full pl-9 pr-4 py-2 bg-[#1A1C18] border border-[#4A5320] text-white text-sm focus:outline-none focus:border-[#FFD700]"
                                      />
                                    </div>
                                    <button
                                      onClick={async () => {
                                        if (donationAmount && parseFloat(donationAmount) > 0) {
                                          try {
                                            await axios.post(`${BACKEND_URL}/api/upcoming-costs/${cost.id}/commit`, {
                                              amount: parseFloat(donationAmount),
                                              name: donorName || null
                                            });
                                            setConfirmedDonation(cost.id);
                                            fetchUpcomingCosts(); // Refresh to show updated totals
                                          } catch (err) {
                                            console.error("Error recording commitment:", err);
                                            setConfirmedDonation(cost.id); // Still show confirmation
                                          }
                                        }
                                      }}
                                      disabled={!donationAmount || parseFloat(donationAmount) <= 0}
                                      className="px-4 py-2 bg-[#FFD700] text-black font-mono text-xs uppercase tracking-widest hover:bg-[#e5c200] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      Confirm
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
            )}
          </div>
          )}

          <p className="text-[#A3A3A3] text-sm text-center mt-8 max-w-2xl mx-auto">
            We believe in complete transparency. 
            100% volunteer-run means every dollar goes directly to animal care.
          </p>
        </div>
      </section>

      {/* Donation Methods */}
      <section className="py-24 bg-[#1A1C18]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Choose Your Method
            </span>
            <h2 className="font-['Bebas_Neue'] text-5xl text-white">
              Ways to Give
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {donationMethods.map((method, index) => (
              <motion.div
                key={method.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`donation-card p-8 ${
                  method.featured
                    ? "bg-[#4A5320]/30 border-2 border-[#FFD700]"
                    : "bg-[#0A0A0A] border border-[#4A5320]"
                }`}
              >
                {method.featured && (
                  <span className="inline-block bg-[#FFD700] text-black font-mono text-xs uppercase tracking-widest px-3 py-1 mb-4">
                    Recommended
                  </span>
                )}
                <div className="w-14 h-14 bg-[#4A5320] flex items-center justify-center mb-6">
                  <method.icon className="w-7 h-7 text-[#FFD700]" />
                </div>
                <h3 className="font-['Bebas_Neue'] text-3xl text-white mb-3">{method.title}</h3>
                <p className="text-[#A3A3A3] text-sm mb-6 leading-relaxed">{method.description}</p>
                <a
                  href={method.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`donate-${method.title.toLowerCase().replace(/\//g, '-')}-button`}
                  className={`w-full inline-flex items-center justify-center gap-2 ${
                    method.featured ? "btn-primary" : "btn-secondary"
                  }`}
                >
                  {method.buttonText}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </motion.div>
            ))}
          </div>

          {/* Additional Methods */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Zelle */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-tactical p-8 flex items-center gap-6"
            >
              <div className="w-14 h-14 bg-[#4A5320] flex items-center justify-center flex-shrink-0">
                <Mail className="w-7 h-7 text-[#FFD700]" />
              </div>
              <div>
                <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-2">Zelle</h3>
                <p className="text-[#A3A3A3] text-sm mb-2">Send directly to:</p>
                <a
                  href="mailto:threeeyeblindrescue@gmail.com"
                  className="text-[#FFD700] font-mono text-sm hover:underline"
                >
                  threeeyeblindrescue@gmail.com
                </a>
              </div>
            </motion.div>

            {/* Mail Check */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-tactical p-8 flex items-center gap-6"
            >
              <div className="w-14 h-14 bg-[#4A5320] flex items-center justify-center flex-shrink-0">
                <MapPin className="w-7 h-7 text-[#FFD700]" />
              </div>
              <div>
                <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-2">Mail a Check</h3>
                <p className="text-[#A3A3A3] text-sm">
                  Three Eye Blind Rescue<br />
                  4010 Foothills Blvd 103-189<br />
                  Roseville, CA 95747
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Amazon Wishlist Section */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Shop For Our Fosters
            </span>
            <h2 className="font-['Bebas_Neue'] text-5xl text-white mb-4">
              Amazon Wishlist
            </h2>
            <p className="text-[#A3A3A3] text-lg max-w-2xl mx-auto">
              Can&apos;t donate cash? You can still help by purchasing items our foster pets need! 
              Browse our wishlist and have items shipped directly to us.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-[#1A1C18] border border-[#4A5320] rounded-lg overflow-hidden"
          >
            <iframe
              src="https://rescuesupportkit.com/rescue/rescue_default_teb/wishlists"
              title="Three Eye Blind Animal Rescue - Amazon Wishlist"
              className="w-full border-0"
              style={{ minHeight: '600px', height: '80vh', maxHeight: '900px' }}
              loading="lazy"
            />
          </motion.div>
        </div>
      </section>

      {/* Danger Zone Teaser */}
      <section id="danger-zone" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700]/10 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-4 mb-6">
                <AlertTriangle className="w-10 h-10 text-[#FFD700]" />
                <span className="font-['Bebas_Neue'] text-5xl text-white">DANGER ZONE</span>
              </div>
              <p className="text-[#A3A3A3] text-lg mb-6 leading-relaxed">
                Named after our founder&apos;s favorite Top Gun moment, the Danger Zone is our exclusive monthly donor membership. Join our squadron of dedicated supporters who provide consistent, reliable funding for our rescue operations.
              </p>
              <p className="text-[#A3A3A3] text-lg mb-8 leading-relaxed">
                As a Danger Zone member, your monthly contribution helps us plan ahead, take on more rescues, and provide the best possible care for every animal. You&apos;re not just donating—you&apos;re becoming part of the mission.
              </p>
              <Link
                to="/danger-zone"
                data-testid="danger-zone-link"
                className="btn-primary inline-flex items-center gap-2"
              >
                Enter the Danger Zone
                <ExternalLink className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-[#1A1C18] border-2 border-[#FFD700] p-8">
                <span className="font-mono text-[#FFD700] text-xs uppercase tracking-widest block mb-4">
                  Monthly Membership Benefits
                </span>
                <h3 className="font-['Bebas_Neue'] text-4xl text-white mb-4">Join the Squadron</h3>
                <ul className="space-y-3 text-[#A3A3A3] text-sm">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#FFD700] mt-2 flex-shrink-0" />
                    Exclusive updates on rescue operations
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#FFD700] mt-2 flex-shrink-0" />
                    Behind-the-scenes content
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#FFD700] mt-2 flex-shrink-0" />
                    Recognition as a Danger Zone member
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#FFD700] mt-2 flex-shrink-0" />
                    The knowledge that you&apos;re saving lives every month
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-24 bg-[#1A1C18]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Your Impact
            </span>
            <h2 className="font-['Bebas_Neue'] text-5xl text-white">
              Where Your Money Goes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { amount: "$50", desc: "Food and supplies for one week" },
              { amount: "$150", desc: "Vaccinations for one pet" },
              { amount: "$350", desc: "Spay/neuter surgery" },
              { amount: "$500+", desc: "Emergency medical care" },
            ].map((item, index) => (
              <motion.div
                key={item.amount}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 border border-[#4A5320]"
              >
                <span className="font-['Bebas_Neue'] text-5xl text-[#FFD700]">{item.amount}</span>
                <p className="text-[#A3A3A3] text-sm mt-2">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tax Deductible Note */}
      <section className="py-16 border-t border-[#4A5320]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#A3A3A3] text-sm font-mono">
            Three Eye Blind Animal Rescue is a registered 501(c)(3) nonprofit organization.<br />
            All donations are tax-deductible. <span className="text-white">EIN: 99-0978241</span>
          </p>
        </div>
      </section>

      {/* Other Ways to Help */}
      <section className="py-24 bg-[#4A5320]/20 border-y border-[#4A5320]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Bebas_Neue'] text-4xl text-white mb-6">
            Other Ways to Help
          </h2>
          <p className="text-[#A3A3A3] text-lg mb-8">
            Can&apos;t donate right now? You can still make a difference!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/applications" className="btn-secondary">
              Foster or Adopt
            </Link>
            <Link to="/store" className="btn-secondary">
              Shop Our Store
            </Link>
            <Link to="/applications#volunteer" className="btn-secondary">
              Volunteer
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
